import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

const ACCENT = '#4A7C59';

interface Batch {
  id: string;
  batchNumber: string;
  processType: string;
  cherryKg: string | null;
  status: string;
  centreName: string | null;
  createdAt: string;
  updatedAt: string;
  notes: string;
}

const STATUS_ORDER = ['cherry_intake', 'fermentation', 'drying', 'milling', 'grading', 'complete'];

const STATUS_STYLE: Record<string, string> = {
  cherry_intake: 'bg-orange-100 text-orange-700',
  fermentation: 'bg-blue-100 text-blue-700',
  drying: 'bg-yellow-100 text-yellow-700',
  milling: 'bg-purple-100 text-purple-700',
  grading: 'bg-pink-100 text-pink-700',
  complete: 'bg-green-100 text-green-700',
};

const STATUS_LABEL: Record<string, string> = {
  cherry_intake: 'Cherry intake',
  fermentation: 'Fermentation',
  drying: 'Drying',
  milling: 'Milling',
  grading: 'Grading',
  complete: 'Complete',
};

const PROCESS_STYLE: Record<string, string> = {
  washed: 'bg-sky-100 text-sky-700',
  natural: 'bg-amber-100 text-amber-700',
  honey: 'bg-rose-100 text-rose-700',
  mbuni: 'bg-yellow-100 text-yellow-800',
};

export const BatchList: React.FC = () => {
  const navigate = useNavigate();
  const [batches, setBatches] = useState<Batch[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [updatingId, setUpdatingId] = useState<string | null>(null);

  const fetchBatches = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({ limit: '50' });
    if (statusFilter) params.set('status', statusFilter);

    try {
      const res = await fetch(`/api/production/batches?${params}`, { credentials: 'include' });
      const data = await res.json();
      setBatches(data.batches || []);
      setTotal(data.total || 0);
    } catch {
      setBatches([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { fetchBatches(); }, [fetchBatches]);

  const advanceStatus = async (batch: Batch) => {
    const currentIdx = STATUS_ORDER.indexOf(batch.status);
    if (currentIdx === -1 || currentIdx === STATUS_ORDER.length - 1) return;
    const nextStatus = STATUS_ORDER[currentIdx + 1];

    setUpdatingId(batch.id);
    try {
      const res = await fetch(`/api/production/batches?id=${batch.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status: nextStatus }),
      });
      if (res.ok) {
        setBatches(bs => bs.map(b => b.id === batch.id ? { ...b, status: nextStatus } : b));
      }
    } finally {
      setUpdatingId(null);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-[#1A1A1A]">Processing batches</h2>
          <p className="text-sm text-[#6B6B6B] mt-0.5">{total} batches</p>
        </div>
        <Link
          to="/production/batches/new"
          className="px-4 py-2.5 text-sm font-medium text-white rounded-lg"
          style={{ backgroundColor: ACCENT }}
        >
          + New batch
        </Link>
      </div>

      {/* Status filter tabs */}
      <div className="flex gap-2 mb-6 flex-wrap">
        {['', ...STATUS_ORDER].map(s => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
              statusFilter === s
                ? 'border-transparent text-white'
                : 'border-[#E8E6E2] text-[#6B6B6B] hover:bg-[#F0EDE8]'
            }`}
            style={statusFilter === s ? { backgroundColor: ACCENT } : {}}
          >
            {s ? STATUS_LABEL[s] : 'All'}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-16 text-center text-sm text-[#6B6B6B]">Loading...</div>
      ) : batches.length === 0 ? (
        <div className="py-16 text-center">
          <p className="text-sm text-[#6B6B6B] mb-3">No batches yet</p>
          <Link to="/production/batches/new" className="text-sm font-medium" style={{ color: ACCENT }}>
            Create the first batch →
          </Link>
        </div>
      ) : (
        <div className="space-y-3">
          {batches.map(batch => {
            const currentIdx = STATUS_ORDER.indexOf(batch.status);
            const canAdvance = currentIdx >= 0 && currentIdx < STATUS_ORDER.length - 1;

            return (
              <Link
                key={batch.id}
                to={`/production/batches/${batch.id}`}
                className="bg-white border border-[#E8E6E2] rounded-xl p-5 flex items-center gap-4 hover:border-[#4A7C59]/40 transition-colors"
              >
                {/* Batch info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-mono text-sm font-medium text-[#1A1A1A]">{batch.batchNumber}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${PROCESS_STYLE[batch.processType] || 'bg-neutral-100 text-neutral-600'}`}>
                      {batch.processType}
                    </span>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_STYLE[batch.status] || 'bg-neutral-100 text-neutral-600'}`}>
                      {STATUS_LABEL[batch.status] || batch.status}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-[#6B6B6B]">
                    {batch.cherryKg && (
                      <span>{parseFloat(batch.cherryKg).toLocaleString()} kg cherry</span>
                    )}
                    {batch.centreName && <span>{batch.centreName}</span>}
                    <span>{new Date(batch.createdAt).toLocaleDateString('en-KE', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                  </div>
                  {batch.notes && (
                    <p className="text-xs text-[#A3A3A3] mt-1 truncate">{batch.notes}</p>
                  )}
                </div>

                {/* Pipeline progress bar */}
                <div className="hidden md:flex items-center gap-1">
                  {STATUS_ORDER.map((s, i) => (
                    <div
                      key={s}
                      className="w-2 h-2 rounded-full"
                      style={{
                        backgroundColor: i <= currentIdx ? ACCENT : '#E8E6E2',
                      }}
                      title={STATUS_LABEL[s]}
                    />
                  ))}
                </div>

                {/* Advance button */}
                {canAdvance && (
                  <button
                    onClick={() => advanceStatus(batch)}
                    disabled={updatingId === batch.id}
                    className="flex-shrink-0 px-3 py-1.5 text-xs font-medium border border-[#E8E6E2] rounded-lg hover:bg-[#F0EDE8] disabled:opacity-50 transition-colors whitespace-nowrap"
                  >
                    {updatingId === batch.id ? '...' : `→ ${STATUS_LABEL[STATUS_ORDER[currentIdx + 1]]}`}
                  </button>
                )}
                {batch.status === 'complete' && (
                  <span className="flex-shrink-0 text-xs text-green-600 font-medium">✓ Complete</span>
                )}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};
